/**
 * Encryption Service
 *
 * Provides AES-256 encryption/decryption for API payloads.
 * Ported from Angular EncryptService to maintain compatibility.
 *
 * Security Note:
 * - Uses CryptoJS with AES-256 encryption
 * - Keys are stored in sessionStorage (vulnerable to XSS)
 * - Consider migrating to httpOnly cookies in the future
 */

import * as CryptoJS from 'crypto-js';

interface EncryptedPayload {
  createdAt: number;
  body: any;
}

class EncryptService {
  /**
   * Encrypt a payload for API request
   * Matches Angular's payload_encode method
   *
   * @param request - The request body to encrypt
   * @returns Encrypted string
   */
  payloadEncode(request: any): string {
    const payload: EncryptedPayload = {
      createdAt: new Date().getTime(),
      body: request,
    };

    // Get encryption key from sessionStorage
    // The key is decrypted from serverString using browserString
    const serverString = sessionStorage.getItem('serverString');
    const browserString = sessionStorage.getItem('browserString');

    if (!serverString || !browserString) {
      console.error('[Encrypt Service] Missing encryption keys in sessionStorage');
      throw new Error('Encryption keys not available');
    }

    const decryptedKey = this.decryptUsingAES256(serverString, browserString);
    const decryptedObject = JSON.parse(decryptedKey);
    const payloadEncryptionKey = decryptedObject;

    // Encrypt the payload
    const _key = CryptoJS.enc.Utf8.parse(JSON.parse(JSON.stringify(payloadEncryptionKey)));
    const _iv = CryptoJS.enc.Utf8.parse('16');

    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), _key, {
      keySize: 16,
      iv: _iv,
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });

    return encrypted.toString();
  }

  /**
   * Decrypt an encrypted string using AES-256
   * Matches Angular's decryptUsingAES256 method
   *
   * @param encryptedString - The encrypted data
   * @param key - The decryption key
   * @returns Decrypted string
   */
  decryptUsingAES256(encryptedString: string, key: string): string {
    const _key = CryptoJS.enc.Utf8.parse(key);
    const _iv = CryptoJS.enc.Utf8.parse('16');

    const decrypted = CryptoJS.AES.decrypt(encryptedString, _key, {
      keySize: 16,
      iv: _iv,
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    }).toString(CryptoJS.enc.Utf8);

    return decrypted;
  }

  /**
   * Check if payload encryption is enabled
   * Checks both envService and sessionStorage (sessionStorage takes precedence)
   *
   * @returns true if encryption is enabled
   */
  isEncryptionEnabled(): boolean {
    const sessionEncryption = sessionStorage.getItem('payloadEncKey');
    return sessionEncryption === 'true';
  }

  /**
   * Check if encryption keys are available in sessionStorage
   *
   * @returns true if all required keys are present
   */
  hasEncryptionKeys(): boolean {
    const serverString = sessionStorage.getItem('serverString');
    const browserString = sessionStorage.getItem('browserString');
    const aainfokey = sessionStorage.getItem('aainfokey');

    return !!(serverString && browserString && aainfokey);
  }
}

// Export singleton instance
export const encryptService = new EncryptService();

// Export class for testing
export { EncryptService };
